import * as THREE from 'three';

class CustomGlobeControls extends THREE.EventDispatcher {
    private camera: THREE.PerspectiveCamera;
    private domElement: HTMLElement;
    private isDragging: boolean;
    private previousMousePosition: { x: number, y: number };
    private globe: THREE.Group;
    private previousDistance?: number;
    private defaultZoomLevel: number;    
    private lastTouchEnd: number;
    private theSea: THREE.Mesh = new THREE.Mesh();
    private previousZoomLevel: number;
    public zoomFactor = 1.1;
    public minZoom = 1.3;
    public maxZoom = 5;
    public dragSpeed = 0.1;
    public mobileDragSpeedMultiplier = 4;

    private toRadians(angle: number): number {
        return angle * (Math.PI / 180);
    }

    private currentVerticalRotation = 0;
    private maxVerticalRotation = this.toRadians(80); // Limit to 80 degrees

    constructor(globe: THREE.Group, camera: THREE.PerspectiveCamera, domElement?: HTMLElement, defaultGlobePosition?: THREE.Vector3,isMobile?: boolean) {
        super();
        
        this.globe = globe;
        this.camera = camera;
        this.domElement = domElement || document.body;
        this.isDragging = false;
        this.previousMousePosition = { x: 0, y: 0 };
        
        if(isMobile) {
            this.camera.position.set(0, 0, 3.45);
        }

        this.defaultZoomLevel = this.camera.position.distanceTo(this.globe.position);
        this.lastTouchEnd = 0;
        this.previousZoomLevel = this.defaultZoomLevel;
        
        for (let i = 0; i < this.globe.children.length; i++) {
            if (this.globe.children[i].name === 'sea') {
                this.theSea = this.globe.children[i] as THREE.Mesh;
                break;
            }
        }

        // Rotate the globe to the default position
        if(defaultGlobePosition) {
            const rotationDifference = defaultGlobePosition.clone().sub(this.globe.position);
            const rotationAngleHorizontal = rotationDifference.y; // Assuming y is the horizontal axis
            const rotationAngleVertical = rotationDifference.x; // Assuming x is the vertical axis
            
            this.globe.rotateY(rotationAngleHorizontal);

            this.globe.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), rotationAngleVertical);

            this.currentVerticalRotation = rotationAngleVertical;
        }  

        this.domElement.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.domElement.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.domElement.addEventListener('mouseup', this.onMouseUp.bind(this));
        this.domElement.addEventListener('wheel', this.onWheel.bind(this));

        this.domElement.addEventListener('touchstart', this.onTouchStart.bind(this));
        this.domElement.addEventListener('touchmove', this.onTouchMove.bind(this));
        this.domElement.addEventListener('touchend', this.onTouchEnd.bind(this));
        this.domElement.addEventListener('touchcancel', this.onTouchCancel.bind(this));
    }

    private onMouseDown(e: MouseEvent) {
        this.isDragging = true;
        this.previousMousePosition = { x: e.clientX, y: e.clientY };
    }

    private onMouseMove(e: MouseEvent) {
        if (!this.isDragging) return;
    
        const deltaMove = {
            x: e.clientX - this.previousMousePosition.x,
            y: e.clientY - this.previousMousePosition.y
        };
    
        // Calculate the current zoom level
        const zoomLevel = this.camera.position.distanceTo(this.globe.position);
    
        // Calculate the zoom factor based on the current and default zoom levels
        const zoomFactor = Math.pow(this.defaultZoomLevel / zoomLevel, 2);
    
        // Adjust the drag speed based on the zoom factor
        const adjustedDragSpeed = this.dragSpeed / zoomFactor;
    
        const rotationAngleHorizontal = this.toRadians(deltaMove.x * adjustedDragSpeed);
        let rotationAngleVertical = this.toRadians(deltaMove.y * adjustedDragSpeed);
    
        // Limit vertical rotation
        if (Math.abs(this.currentVerticalRotation + rotationAngleVertical) > this.maxVerticalRotation) {
            rotationAngleVertical = this.maxVerticalRotation * Math.sign(this.currentVerticalRotation + rotationAngleVertical) - this.currentVerticalRotation;
        }
        this.currentVerticalRotation += rotationAngleVertical;  

        this.globe.rotateY(rotationAngleHorizontal);

        this.globe.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), rotationAngleVertical);

        this.previousMousePosition = { x: e.clientX, y: e.clientY };
    }

    private onMouseUp(e: MouseEvent) {
        this.isDragging = false;
    }

    private onWheel(e: WheelEvent) {
        e.preventDefault();
    
        // Calculate the new zoom level
        const zoomLevel = e.deltaY > 0 ? this.zoomFactor : 1 / this.zoomFactor;
    
        // Calculate the new position
        const newPosition = this.camera.position.clone().multiplyScalar(zoomLevel);
    
        // Check if the new position is within the zoom limits
        if (newPosition.length() >= this.minZoom && newPosition.length() <= this.maxZoom) {
            // Update the camera's position
            this.camera.position.copy(newPosition);
        }
    }

    private onTouchStart(e: TouchEvent) {
        this.isDragging = true;
        this.previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }

    private onTouchMove(e: TouchEvent) {
        e.preventDefault();

        if (!this.isDragging) return;
    
        // If there are two touch points, it's likely a pinch gesture
        if (e.touches.length === 2) {
            // Calculate the distance between the two touch points
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const distance = Math.sqrt(dx * dx + dy * dy);
    
            // If the previous distance is defined, calculate the zoom level
        if (this.previousDistance) {
            const zoomLevel = this.previousDistance / distance;

            // Calculate the new position
            const newPosition = this.camera.position.clone().multiplyScalar(zoomLevel);

            // Check if the new position is within the zoom limits
            if (newPosition.length() >= this.minZoom && newPosition.length() <= this.maxZoom) {
                // Update the camera's position
                this.camera.position.copy(newPosition);
            }
        }
    
            // Store the current distance for the next touch move event
            this.previousDistance = distance;
    
            return;
        }
    
        const deltaMove = {
            x: e.touches[0].clientX - this.previousMousePosition.x,
            y: e.touches[0].clientY - this.previousMousePosition.y
        };
    
        // Calculate the current zoom level
        const zoomLevel = this.camera.position.length();
    
        // Calculate the zoom factor based on the current and default zoom levels
        const zoomFactor = Math.pow(this.defaultZoomLevel / zoomLevel, 2);
    
        // Adjust the drag speed based on the zoom factor
        const adjustedDragSpeed = this.dragSpeed / zoomFactor;
    
        const rotationAngleHorizontal = this.toRadians(deltaMove.x * adjustedDragSpeed * this.mobileDragSpeedMultiplier);
        let rotationAngleVertical = this.toRadians(deltaMove.y * adjustedDragSpeed * this.mobileDragSpeedMultiplier);
    
        // Limit vertical rotation
        if (Math.abs(this.currentVerticalRotation + rotationAngleVertical) > this.maxVerticalRotation) {
            rotationAngleVertical = this.maxVerticalRotation * Math.sign(this.currentVerticalRotation + rotationAngleVertical) - this.currentVerticalRotation;
        }
        this.currentVerticalRotation += rotationAngleVertical;

        this.globe.rotateY(rotationAngleHorizontal);
    
        // this.globe.children.forEach((child) => {
        //     if (child instanceof THREE.Mesh) {
        //         child.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), rotationAngleHorizontal);
        //     }
        // });
    
        this.globe.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), rotationAngleVertical);
        this.previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }

    private onTouchEnd(e: TouchEvent) {
        this.isDragging = false;
        this.previousDistance = undefined;
        const now = (new Date()).getTime();
        if (now - this.lastTouchEnd <= 300) {
            e.preventDefault();
        }
        this.lastTouchEnd = now;
    }
    
    private onTouchCancel(e: TouchEvent) {
        this.isDragging = false;
        this.previousDistance = undefined;
    }

    public centerCameraOnObject(object: THREE.Mesh, animationDuration: number) {
        // Ensure the object's geometry has a bounding sphere
        if (!object.geometry.boundingSphere) {
            return;
        }

        const boundingSphere = object.geometry.boundingSphere;
    
        const previousGlobeQuaternion = this.globe.quaternion.clone();
        
        // Reset the globe's rotation
        this.globe.rotation.set(0, 0, 0);
    
        // Get the center of the bounding sphere
        const center = boundingSphere.center.clone();
        
        // Convert the center to world coordinates
        object.localToWorld(center);
        
        // Calculate the spherical coordinates of the center of the object
        const spherical = new THREE.Spherical().setFromVector3(center);
        
        // Convert the spherical coordinates to latitude and longitude
        const latitude = 90 - (spherical.phi * 180 / Math.PI);
        const longitude = spherical.theta * 180 / Math.PI;
        
        // Apply the rotations
        const horizontalRotation = this.toRadians(-longitude);
        const verticalRotation = this.toRadians(latitude);
        this.globe.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), verticalRotation);
        this.globe.rotateY(horizontalRotation);
    
        const newGlobeQuaternion = this.globe.quaternion.clone();

        this.currentVerticalRotation = verticalRotation;

        // Calculate the required zoom level
        const objectSize = boundingSphere.radius * 2;
        const aspectRatio = this.camera.aspect;
        const cameraFov = this.toRadians(this.camera.fov);
        
        const globeRadius = this.theSea && this.theSea.geometry && this.theSea.geometry.boundingSphere ? this.theSea.geometry.boundingSphere.radius : 1.25;

        // Determine the maximum dimension (height or width) that needs to fit in the viewport
        const maxDimension = Math.max(objectSize / aspectRatio, objectSize);

        // Calculate the distance required to fit the object in the viewport
        let zoomLevel = (maxDimension / 2) / Math.tan(cameraFov / 2);
        this.previousZoomLevel = this.camera.position.length();

        zoomLevel += globeRadius; // Add the radius of the globe


        if (zoomLevel < this.minZoom * 1.1) {
            zoomLevel = this.minZoom * 1.1;
        } else if (zoomLevel > this.maxZoom) {
            zoomLevel = this.maxZoom;
        }
    
        // Animate the globe rotation from the previous rotation to the new rotation
        this.animateGlobeQuaternion(previousGlobeQuaternion, newGlobeQuaternion, zoomLevel, animationDuration);
    }
    
    private animateGlobeQuaternion(previousQuaternion: THREE.Quaternion, newQuaternion: THREE.Quaternion, zoomLevel: number, animationDuration: number) {
        // Animate the globe rotation from the previous rotation to the new rotation
        const startTime = Date.now();
        const previousCameraPosition = this.camera.position.clone();

        const newCameraPosition = new THREE.Vector3(0, 0, zoomLevel)

        const easingFunction = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOutQuad

        const animate = () => {
            const elapsedTime = Date.now() - startTime;
            const t = Math.min(elapsedTime / animationDuration, 1);
            const progress = easingFunction(t);

            // Interpolate the rotation using slerp
            this.globe.quaternion.slerpQuaternions(previousQuaternion, newQuaternion, progress);

            // Interpolate the camera position
            this.camera.position.lerpVectors(previousCameraPosition, newCameraPosition, progress);

            if (t < 1) {
                requestAnimationFrame(animate);
            }
        };
        animate();
    }

    public animateCameraZoomReset(animationDuration: number) {
        // Animate the camera zoom to the default zoom level
        const startTime = Date.now();
        const previousCameraPosition = this.camera.position.clone();
        const newCameraPosition = new THREE.Vector3(0, 0, this.previousZoomLevel);

        const easingFunction = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOutQuad

        const animate = () => {
            const elapsedTime = Date.now() - startTime;
            const t = Math.min(elapsedTime / animationDuration, 1);
            const progress = easingFunction(t);

            // Interpolate the camera position
            this.camera.position.lerpVectors(previousCameraPosition, newCameraPosition, progress);

            if (t < 1) {
                requestAnimationFrame(animate);
            }
        };
        animate();
    }
}

export { CustomGlobeControls };